import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Target, Plus, RefreshCcw, Users, DollarSign, Layers, CheckCircle, XCircle, FileText, Trash2 } from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';
import { QawaemTableStickyActionsTd } from '../../products/saudi/components/QawaemTableStickyActions';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import { useTranslation } from 'react-i18next';
import { useCrmPage, useCrmData, getValueTier, resolveCrmModalTabs } from '../../shared/utils/crmPageUtils';
import { enrichStatusOptions } from '../../shared/utils/crmPageUtils';
import { getBrandRegionOptions, getRegionLabel } from '../../shared/utils/brandRegions';
import { CRM_SEGMENT_TYPES } from '../../shared/utils/crmConstants';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import QawaemErpCrudModal from '../../products/saudi/components/QawaemErpCrudModal';
import {
  CrmModalFormTabs,
  CrmModalFormBody,
  CrmModalFormPanel,
  CrmModalFieldGrid,
} from './components/CrmModalFormTabs';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import { useCrmConfig } from './useCrmConfig';
import { getCrmPriceTierThresholds } from './crmConfigDefaults';
import { filterCrmRecordsByDateRange, sliceCrmListItems } from './crmListPageUtils';

const SEGMENT_TABLE_COLUMNS = [
  { key: 'ref', labelKey: 'col_ref' },
  { key: 'name', labelKey: 'col_name' },
  { key: 'type', labelKey: 'col_type' },
  { key: 'customers', labelKey: 'col_customers' },
  { key: 'revenue', labelKey: 'col_revenue' },
  { key: 'avg_order', labelKey: 'col_avg_order' },
  { key: 'priority', labelKey: 'col_priority' },
  { key: 'status', labelKey: 'col_status', align: 'center' },
];

const PRIORITY_OPTIONS = [
  { value: 'high', labelKey: 'priority_high' },
  { value: 'medium', labelKey: 'priority_medium' },
  { value: 'low', labelKey: 'priority_low' },
];

const emptyForm = () => ({
  segment_name: '', segment_type: 'value', criteria: '', customer_count: '',
  total_revenue: '', avg_order_value: '', priority_level: 'medium',
  marketing_strategy: '', status: 'active', notes: '',
});

const FORM_ID = 'crm-segments-form';
const SEGMENT_MODAL_FIELD_COUNT = 10;

const SegmentsPage = () => {
  const { t } = useTranslation();
  const cfgTheme = useModuleConfigTheme();
  const crmConfig = useCrmConfig();
  const { dateRange } = useSaudiListDateRange();
  const { p, fmtMoney } = useCrmPage('segmentsPage');
  const priceTiers = useMemo(() => getCrmPriceTierThresholds(crmConfig), [crmConfig]);
  const { items: segments, load, save, remove } = useCrmData('/crm/segmentation');
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityLabel = p('entity_label', { defaultValue: p('title') });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: entityLabel });
  const regionOptions = useMemo(() => getBrandRegionOptions(), []);
  const typeOptions = useMemo(() => CRM_SEGMENT_TYPES.map((t) => ({ value: t.value, label: t.label })), []);

  const [searchTerm, setSearchTerm] = useState('');
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [formTab, setFormTab] = useState('definition');
  const [columnVisibility, setColumnVisibility] = useState(() =>
    Object.fromEntries(SEGMENT_TABLE_COLUMNS.map((c) => [c.key, true]))
  );

  const qawaemColumnDefs = useMemo(
    () => SEGMENT_TABLE_COLUMNS.map((col) => ({
      key: col.key,
      label: p(col.labelKey),
      checked: columnVisibility[col.key] !== false,
    })),
    [columnVisibility, p],
  );
  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const visibleColumns = useMemo(
    () => SEGMENT_TABLE_COLUMNS.filter((c) => columnVisibility[c.key] !== false),
    [columnVisibility],
  );
  const tableColSpan = visibleColumns.length + 2;

  const priorityLabel = (v) => p(PRIORITY_OPTIONS.find((x) => x.value === v)?.labelKey || 'priority_medium');

  const getSegmentFilterValue = (s, colKey) => {
    switch (colKey) {
      case 'ref': return s.segment_ref || '';
      case 'name': return s.segment_name || '';
      case 'type': return typeOptions.find((t) => t.value === s.segment_type)?.label || s.segment_type || '';
      case 'customers': return String(s.customer_count || 0);
      case 'revenue': return fmtMoney(s.total_revenue);
      case 'avg_order': return fmtMoney(s.avg_order_value);
      case 'priority': return priorityLabel(s.priority_level);
      case 'status': return s.status === 'active' ? p('stat_active') : (p('status_inactive') || 'غير نشط');
      default: return '';
    }
  };

  const columnFilterRules = useMemo(
    () => visibleColumns.map((col) => {
      const getValue = (row) => getSegmentFilterValue(row, col.key);
      if (col.key === 'status' || col.key === 'type' || col.key === 'priority') return exactColumnRule(col.key, getValue);
      return textColumnRule(col.key, getValue);
    }),
    [visibleColumns, typeOptions, p, fmtMoney],
  );

  const searchFiltered = useMemo(() => {
    const dated = filterCrmRecordsByDateRange(segments, dateRange, ['created_at', 'updated_at']);
    return dated.filter((s) =>
      !searchTerm || s.segment_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [segments, searchTerm, dateRange]);

  const filtered = useMemo(
    () => applyColumnFilters(searchFiltered, colFilters, columnFilterRules),
    [searchFiltered, colFilters, columnFilterRules],
  );

  const pagedFiltered = useMemo(
    () => sliceCrmListItems(filtered, crmConfig.itemsPerPage),
    [filtered, crmConfig.itemsPerPage],
  );

  const {
    selectedRecords,
    setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
  } = useQawaemFilteredRowSelection(filtered);

  useEffect(() => {
    if (selectedRecords.length > 0) setEditingKey(null);
  }, [selectedRecords, setEditingKey]);

  const handleBulkDelete = async () => {
    if (!selectedRecords.length || !window.confirm(p('delete_confirm'))) return;
    try {
      const deleteCount = selectedRecords.length;
      await Promise.all(selectedRecords.map((id) => remove(id)));
      notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
      setSelectedRecords([]);
    } catch {
      notifyError(showNotify, p('save_error'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(p('delete_confirm'))) return;
    try {
      await remove(id);
      notifyDeleted(showNotify, entityLabel);
    } catch {
      notifyError(showNotify, p('save_error'));
    }
  };

  const stats = useMemo(() => ({
    total: segments.length,
    active: segments.filter((s) => s.status === 'active').length,
    customers: segments.reduce((sum, s) => sum + (parseInt(s.customer_count, 10) || 0), 0),
    revenue: segments.reduce((sum, s) => sum + (parseFloat(s.total_revenue) || 0), 0),
  }), [segments]);

  const statusOptions = useMemo(() => enrichStatusOptions([
    { value: 'active', label: p('stat_active'), icon: CheckCircle },
    { value: 'inactive', label: p('status_inactive') || 'غير نشط', icon: XCircle },
  ]), [p]);

  const handleStatusChange = async (recordId, newStatus) => {
    const segment = segments.find((s) => s.id === recordId);
    if (!segment) return;
    await save({ ...segment, status: newStatus }, recordId);
  };

  const renderSegmentCell = (s, col) => {
    switch (col.key) {
      case 'ref':
        return <td key={col.key} className="px-4 py-3 text-white/80 text-sm">{s.segment_ref || '—'}</td>;
      case 'name':
        return (
          <td key={col.key} className="px-4 py-3">
            <div className="text-white font-medium">{s.segment_name}</div>
            <div className="text-white/50 text-xs mt-0.5">{getValueTier(s.total_revenue, priceTiers, p)}</div>
          </td>
        );
      case 'type':
        return <td key={col.key} className="px-4 py-3 text-white/70 text-sm">{typeOptions.find((t) => t.value === s.segment_type)?.label || s.segment_type}</td>;
      case 'customers':
        return <td key={col.key} className="px-4 py-3 text-white">{s.customer_count || 0}</td>;
      case 'revenue':
        return <td key={col.key} className="px-4 py-3 text-white font-semibold">{fmtMoney(s.total_revenue)}</td>;
      case 'avg_order':
        return <td key={col.key} className="px-4 py-3 text-white/80">{fmtMoney(s.avg_order_value)}</td>;
      case 'priority':
        return <td key={col.key} className="px-4 py-3 text-white/70">{priorityLabel(s.priority_level)}</td>;
      case 'status':
        return (
          <td key={col.key} className="px-4 py-3 text-center">
            <div className="flex justify-center">
              <QawaemClickableStatusBadge
                status={s.status || 'active'}
                recordId={s.id}
                options={statusOptions}
                onChange={handleStatusChange}
                title={p('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
              />
            </div>
          </td>
        );
      default:
        return null;
    }
  };

  const segmentFormTabs = useMemo(() => resolveCrmModalTabs(SEGMENT_MODAL_FIELD_COUNT, [
    { id: 'definition', label: p('tab_definition', { defaultValue: 'التعريف' }), icon: Target },
    { id: 'details', label: p('tab_details', { defaultValue: 'التفاصيل' }), icon: FileText, iconClassName: 'text-purple-400' },
  ]), [p]);
  const segmentDefaultTab = segmentFormTabs[0]?.id || 'definition';

  const closeModal = () => {
    setFormTab(segmentDefaultTab);
    setShowModal(false);
  };

  const openModal = (record = null) => {
    setEditing(record);
    setForm(record ? { ...record } : emptyForm());
    setFormTab(segmentDefaultTab);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await save(form, editing?.id);
      if (editing?.id) {
        notifyUpdated(showNotify, entityLabel);
      } else {
        notifyCreated(showNotify, entityLabel);
      }
      closeModal();
    } catch {
      notifyError(showNotify, p('save_error'));
    }
  };

  return (
    <div className="space-y-6 employees-container">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Target className="h-8 w-8 text-cyan-400" />
            {p('title')}
            <button type="button" onClick={load} className="refresh-button"><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
        <button type="button" onClick={() => openModal()}
          className="bg-gradient-to-r from-cyan-500 to-cyan-700 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2">
          <Plus size={20} />{p('add')}
        </button>
        {selectedRecords.length > 0 ? (
          <button
            type="button"
            onClick={handleBulkDelete}
            className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-200 hover:bg-red-500/20 transition-colors text-sm font-medium"
          >
            <Trash2 size={16} />
            <span>{t('common.delete')} ({selectedRecords.length})</span>
          </button>
        ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { title: p('stat_total'), value: stats.total, icon: Layers },
          { title: p('stat_active'), value: stats.active, icon: Target },
          { title: p('stat_customers'), value: stats.customers, icon: Users },
          { title: p('stat_revenue'), value: fmtMoney(stats.revenue), icon: DollarSign },
        ].slice(0, 4).map((s, i) => (
          <motion.div key={s.title} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="bg-white/10 backdrop-blur-lg rounded-2xl p-5 border border-white/20">
            <p className="text-white/60 text-sm">{s.title}</p>
            <p className="text-2xl font-bold text-white mt-1">{s.value}</p>
          </motion.div>
        ))}
      </div>

      <div className="glass-panel glass-table-shell overflow-visible">
        
        <div className="glass-table-scroll overflow-x-auto">
          <table className="w-full employees-table">
            <thead className="glass-table-head">
              <tr>
                <QawaemTableSelectAllHeader
                  allRowsSelected={allRowsSelected}
                  someRowsSelected={someRowsSelected}
                  onChange={handleSelectAll}
                />
                {visibleColumns.map((col) => (
                  <TableColumnHeaderFilter
                    key={col.key}
                    columnKey={col.key}
                    label={p(col.labelKey)}
                    kind={col.key === 'status' || col.key === 'type' || col.key === 'priority' ? 'select' : 'text'}
                    value={colFilters[col.key] ?? ''}
                    onChange={(v) => setColFilter(col.key, v)}
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    align={col.align === 'center' ? 'center' : undefined}
                    thClassName="text-white font-semibold text-sm"
                    className="px-4 py-3"
                  />
                ))}
                <QawaemTableHeaderActionsCell
                  label={p('col_actions')}
                  columns={qawaemColumnDefs}
                  onToggle={handleToggleColumn}
                  visibleCount={qawaemVisibleColumnCount}
                  totalCount={qawaemColumnDefs.length}
                  thClassName="px-4 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {filtered.length ? pagedFiltered.map((s) => (
                <tr key={s.id} className={`hover:bg-white/5 ${selectedRecords.includes(s.id) ? 'bg-white/10' : ''}`}>
                  <QawaemTableSelectRowCell
                    checked={selectedRecords.includes(s.id)}
                    onChange={() => handleSelectRecord(s.id)}
                  />
                  {visibleColumns.map((col) => renderSegmentCell(s, col))}
                  <QawaemTableStickyActionsTd>
                    <QawaemErpRowActions
                      onEdit={() => openModal(s)}
                      onDelete={() => handleDelete(s.id)}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </QawaemTableStickyActionsTd>
                </tr>
              )) : (
                <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60">{p('empty')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={closeModal}
          title={editing ? p('modal_edit') : p('modal_new')}
          icon={Target}
          formId={FORM_ID}
          saveLabel={t('common.save')}
          onSubmit
          maxWidth="1200px"
          testId="crm-segments-modal"
        >
          {segmentFormTabs.length > 0 ? (
            <CrmModalFormBody>
              <CrmModalFormTabs
                tabs={segmentFormTabs}
                activeTab={formTab}
                onTabChange={setFormTab}
                ariaLabel={editing ? p('modal_edit') : p('modal_new')}
              />
              <CrmModalFormPanel>
                <form id={FORM_ID} onSubmit={handleSubmit}>
                  {formTab === 'definition' && (
                    <CrmModalFieldGrid>
                      <div>
                        <label className={cfgTheme.label}>{p('field_name')}<span className="text-red-600 font-bold"> *</span></label>
                        <input required value={form.segment_name} onChange={(e) => setForm({ ...form, segment_name: e.target.value })} className={cfgTheme.input} />
                      </div>
                      <div>
                        <label className={cfgTheme.label}>{p('field_type')}</label>
                        <CustomSelect value={form.segment_type} onChange={(v) => setForm({ ...form, segment_type: v })} options={typeOptions} />
                      </div>
                      <div>
                        <label className={cfgTheme.label}>{p('field_region')}</label>
                        <CustomSelect value={form.criteria} onChange={(v) => setForm({ ...form, criteria: v })} options={regionOptions} placeholder={getRegionLabel(form.criteria)} />
                      </div>
                      <div>
                        <label className={cfgTheme.label}>{p('field_priority')}</label>
                        <CustomSelect value={form.priority_level} onChange={(v) => setForm({ ...form, priority_level: v })}
                          options={PRIORITY_OPTIONS.map((o) => ({ value: o.value, label: p(o.labelKey) }))} />
                      </div>
                      <div>
                        <label className={cfgTheme.label}>{p('col_status')}</label>
                        <CustomSelect value={form.status || 'active'} onChange={(v) => setForm({ ...form, status: v })} options={statusOptions.map((s) => ({ value: s.value, label: s.label }))} />
                      </div>
                    </CrmModalFieldGrid>
                  )}
                  {formTab === 'details' && (
                    <CrmModalFieldGrid>
                      <div>
                        <label className={cfgTheme.label}>{p('field_customers', { defaultValue: 'عدد العملاء' })}</label>
                        <input type="number" value={form.customer_count || ''} onChange={(e) => setForm({ ...form, customer_count: e.target.value })} className={cfgTheme.input} />
                      </div>
                      <div>
                        <label className={cfgTheme.label}>{p('field_revenue', { defaultValue: 'إجمالي الإيرادات' })}</label>
                        <input type="number" step="0.01" value={form.total_revenue || ''} onChange={(e) => setForm({ ...form, total_revenue: e.target.value })} className={cfgTheme.input} />
                      </div>
                      <div>
                        <label className={cfgTheme.label}>{p('field_avg_order', { defaultValue: 'متوسط الطلب' })}</label>
                        <input type="number" step="0.01" value={form.avg_order_value || ''} onChange={(e) => setForm({ ...form, avg_order_value: e.target.value })} className={cfgTheme.input} />
                      </div>
                      <div className="md:col-span-3">
                        <label className={cfgTheme.label}>{p('field_strategy')}</label>
                        <textarea value={form.marketing_strategy || ''} onChange={(e) => setForm({ ...form, marketing_strategy: e.target.value })} rows={3}
                          className={`${cfgTheme.input} resize-none`} />
                      </div>
                      <div className="md:col-span-3">
                        <label className={cfgTheme.label}>{p('field_notes', { defaultValue: 'ملاحظات' })}</label>
                        <textarea value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3}
                          className={`${cfgTheme.input} resize-none`} />
                      </div>
                    </CrmModalFieldGrid>
                  )}
                </form>
              </CrmModalFormPanel>
            </CrmModalFormBody>
          ) : (
            <form id={FORM_ID} onSubmit={handleSubmit}>
              <CrmModalFieldGrid>
                <div>
                  <label className={cfgTheme.label}>{p('field_name')}<span className="text-red-600 font-bold"> *</span></label>
                  <input required value={form.segment_name} onChange={(e) => setForm({ ...form, segment_name: e.target.value })} className={cfgTheme.input} />
                </div>
                <div>
                  <label className={cfgTheme.label}>{p('field_type')}</label>
                  <CustomSelect value={form.segment_type} onChange={(v) => setForm({ ...form, segment_type: v })} options={typeOptions} />
                </div>
                <div>
                  <label className={cfgTheme.label}>{p('field_region')}</label>
                  <CustomSelect value={form.criteria} onChange={(v) => setForm({ ...form, criteria: v })} options={regionOptions} placeholder={getRegionLabel(form.criteria)} />
                </div>
                <div>
                  <label className={cfgTheme.label}>{p('field_priority')}</label>
                  <CustomSelect value={form.priority_level} onChange={(v) => setForm({ ...form, priority_level: v })}
                    options={PRIORITY_OPTIONS.map((o) => ({ value: o.value, label: p(o.labelKey) }))} />
                </div>
                <div>
                  <label className={cfgTheme.label}>{p('col_status')}</label>
                  <CustomSelect value={form.status || 'active'} onChange={(v) => setForm({ ...form, status: v })} options={statusOptions.map((s) => ({ value: s.value, label: s.label }))} />
                </div>
                <div>
                  <label className={cfgTheme.label}>{p('field_customers', { defaultValue: 'عدد العملاء' })}</label>
                  <input type="number" value={form.customer_count || ''} onChange={(e) => setForm({ ...form, customer_count: e.target.value })} className={cfgTheme.input} />
                </div>
                <div>
                  <label className={cfgTheme.label}>{p('field_revenue', { defaultValue: 'إجمالي الإيرادات' })}</label>
                  <input type="number" step="0.01" value={form.total_revenue || ''} onChange={(e) => setForm({ ...form, total_revenue: e.target.value })} className={cfgTheme.input} />
                </div>
                <div>
                  <label className={cfgTheme.label}>{p('field_avg_order', { defaultValue: 'متوسط الطلب' })}</label>
                  <input type="number" step="0.01" value={form.avg_order_value || ''} onChange={(e) => setForm({ ...form, avg_order_value: e.target.value })} className={cfgTheme.input} />
                </div>
                <div className="md:col-span-3">
                  <label className={cfgTheme.label}>{p('field_strategy')}</label>
                  <textarea value={form.marketing_strategy || ''} onChange={(e) => setForm({ ...form, marketing_strategy: e.target.value })} rows={3}
                    className={`${cfgTheme.input} resize-none`} />
                </div>
                <div className="md:col-span-3">
                  <label className={cfgTheme.label}>{p('field_notes', { defaultValue: 'ملاحظات' })}</label>
                  <textarea value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3}
                    className={`${cfgTheme.input} resize-none`} />
                </div>
              </CrmModalFieldGrid>
            </form>
          )}
        </QawaemErpCrudModal>
      )}

      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default SegmentsPage;
